import { apiClient } from '../../lib/api-client';
import { PATIENT_UHID_SEARCH_LIMIT } from '../../lib/patient-uhid-search';
import type {
  CreateEmgRegistrationPayload,
  EmgListFilters,
  EmgLookupOption,
  EmgPayment,
  EmgPrescription,
  EmgRegistration,
  Pagination,
  UpdateEmgRegistrationPayload,
  UpsertEmgPrescriptionPayload,
} from './types';

interface ApiResponse<T> { success: true; data: T }
interface PaginatedResponse<T> extends ApiResponse<T[]> { pagination: Pagination }

const compactParams = (params: object) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== '' && value !== undefined && value !== null),
);

export const fetchEmgRegistrations = async (filters: EmgListFilters) => {
  const response = await apiClient.get<PaginatedResponse<EmgRegistration>>('/emg/registrations', { params: compactParams(filters) });
  return response.data;
};

export const fetchEmgRegistration = async (id: string) => {
  const response = await apiClient.get<ApiResponse<EmgRegistration>>(`/emg/registrations/${id}`);
  return response.data.data;
};

export const createEmgRegistration = async (payload: CreateEmgRegistrationPayload) => {
  const response = await apiClient.post<ApiResponse<EmgRegistration>>('/emg/registrations', payload);
  return response.data.data;
};

export const updateEmgRegistration = async (id: string, payload: UpdateEmgRegistrationPayload) => {
  const response = await apiClient.patch<ApiResponse<EmgRegistration>>(`/emg/registrations/${id}`, payload);
  return response.data.data;
};

export const updateEmgRegistrationPayment = async (id: string, paymentIndex: number, payload: { mode: EmgPayment['mode']; bankName: string | null; amount: number }) => {
  const response = await apiClient.patch<ApiResponse<EmgRegistration>>(`/emg/registrations/${id}/payments/${paymentIndex}`, payload);
  return response.data.data;
};

export const addEmgRegistrationPayment = async (id: string, payload: { mode: EmgPayment['mode']; bankName: string | null; amount: number }) => {
  const response = await apiClient.post<ApiResponse<EmgRegistration>>(`/emg/registrations/${id}/payments`, payload);
  return response.data.data;
};

export const addEmgRegistrationRefund = async (id: string, payload: { mode: EmgPayment['mode']; bankName: string | null; amount: number }) => {
  const response = await apiClient.post<ApiResponse<EmgRegistration>>(`/emg/registrations/${id}/refunds`, payload);
  return response.data.data;
};

export const cancelEmgRegistrationBill = async (id: string) => {
  const response = await apiClient.post<ApiResponse<EmgRegistration>>(`/emg/registrations/${id}/cancel`);
  return response.data.data;
};

export const fetchEmgPrescription = async (registrationId: string) => {
  const response = await apiClient.get<ApiResponse<EmgPrescription>>(`/emg/registrations/${registrationId}/prescription`);
  return response.data.data;
};

export const saveEmgPrescription = async (registrationId: string, payload: UpsertEmgPrescriptionPayload) => {
  const response = await apiClient.put<ApiResponse<EmgPrescription>>(`/emg/registrations/${registrationId}/prescription`, payload);
  return response.data.data;
};

export const fetchEmgLookups = async (type: string, search = '', state = '') => {
  const patientLookup = type === 'patients' || type.startsWith('appointment-patients');
  const response = await apiClient.get<ApiResponse<EmgLookupOption[]>>('/emg/lookups', { params: compactParams({ type, search, state, limit: patientLookup ? PATIENT_UHID_SEARCH_LIMIT : 500 }) });
  return response.data.data;
};
